import React, { useContext, useEffect, useState } from 'react';
import fileCheckSvg from 'assets/images/file-check-green.svg';
import { AutoComplete, Divider, Form, Input, Space, Button } from 'antd';
import { File, Task, TaskState } from 'gql/graphql';
import DateInput from 'rsuite/DateInput';
import 'rsuite/DateInput/styles/index.css';

import InputBorderNone from 'components/Atoms/Inputs/InputBorderNone/InputBorderNone';
import InputUploadedFile from 'components/Atoms/Inputs/InputUploadedFile/InputUploadedFile';
import {
  formatterSsn as utilsFormatterSsn,
  formatterPhone as utilsFormatterPhone,
  formatterDate as utilsFormatterDate
} from '../../../../../../../@common/utils/formatters/formatter';
import ModalSubmitProfile from './ModalSubmitProfile';
import { Ownership } from './CardOwnership';
import { useDeleteFileResponse } from '../../../../../../../services/task/mutations';
import UploadDocs from '../../../../../../../components/Atoms/UploadDocs/UploadDocs';
import { InputAutocompleteAddress } from 'components/Atoms/Inputs/InputAutocompleteAddress/InputAutocompleteAddress';
import iconSpinnerPurple from 'assets/images/spinner-purple.svg';
import checkGreenSvg from 'assets/images/check-green.svg';
import { validatorDateError } from '@common/utils/validators';

interface Props {
  ownership: Ownership;
  disabledForm?: boolean;
  saveEdit?: string;
  fetching: boolean;
  saveChanges: Function;
  ownerships?: Ownership[];
  verifyProfile: any;
  isExpand?: any;
  createResponse?: any;
  taskComments?: any[];
}

const FormEdit = ({
  ownership,
  disabledForm,
  saveChanges,
  saveEdit,
  fetching,
  ownerships,
  verifyProfile,
  isExpand,
  createResponse,
  taskComments
}: Props) => {
  const [form] = Form.useForm();
  const { fetching: loadingDeleteFile, execute: _deleteFile } = useDeleteFileResponse();


  const [listFileUpload, setListFileUpload] = useState<any>([]);
  const [isTextDataError, setTextDataError] = useState<boolean>();
  const [isEditVerify, setEditVerify] = useState<boolean>(false);
  const [isChangeExpand, setIsChangeExpand] = useState<boolean>(false);

  useEffect(() => {
    if (taskComments) {
      const issue = taskComments?.find(item => item.taskResponse._id === ownership.taskResponse._id && ownership.state === TaskState.RequestError);
      let textDataError = issue?.metadata?.dataOrDocumentSelected.includes('Text/data');

      if ((!issue || textDataError === false) && isExpand !== isChangeExpand) {
        form.setFieldsValue(getInitialValues());
      }

      setTextDataError(textDataError);

      //Se soluciona el error para poder editar diferentes formularios sin que se borre info
      if (isExpand !== isChangeExpand)
        setIsChangeExpand(isExpand);
    }

    setListFileUpload([]);
  }, [isExpand, taskComments]);

  useEffect(() => {
    if (createResponse) setListFileUpload([]);
  }, [createResponse])

  useEffect(() => {
    if (listFileUpload?.length > 0 && !isFormDisabled())
      saveChanges(form.getFieldsValue(), listFileUpload);
  }, [listFileUpload])

  const getInitialValues = () => {
    return ({
      ...ownership,
      dateBirth: ownership.dateBirth ? new Date(ownership.dateBirth) : null,
      ssn: utilsFormatterSsn(ownership.ssn),
      phone: utilsFormatterPhone(ownership.phone)
    })
  };

  const formatterSsn = (e: any) => {
    form.setFieldValue('ssn', utilsFormatterSsn(e.target.value));
  };

  const formatterPhone = (e: any) => {
    form.setFieldValue('phone', utilsFormatterPhone(e.target.value));
  };

  const isFormDisabled = () => {
    const isFieldsValidate = Object.values(form.getFieldsValue()).every(
      (value) => !!value
    );

    return (
      !isFieldsValidate ||
      form.getFieldsError().filter(({ errors }) => errors.length)?.length > 0 ||
      (listFileUpload?.length === 0 && ownership?.files?.length === 0)
    );
  };

  const isFormComplete = () => {
    const values = form.getFieldsValue();
    const requiredFields = ['ssn', 'dateBirth', 'phone', 'email', 'address'];

    // Verificar que todos los campos requeridos tengan valor
    const allFieldsFilled = requiredFields.every(field => {
      const value = values[field];
      return value !== null && value !== undefined && value !== '';
    });

    // Verificar que no haya errores de validación
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length > 0);

    // Verificar que haya al menos un archivo
    const hasFiles = (listFileUpload?.length > 0 || ownership?.files?.filter(file => file.state !== 'request_error')?.length > 0);

    return allFieldsFilled && !hasErrors && hasFiles;
  };

  const saveResponse = async (fieldData: any, isComplete: boolean = false) => {
    const filesToSave = listFileUpload?.length > 0 ? listFileUpload : ownership?.files?.filter(file => file.state !== 'request_error');
    if (isComplete) {
      await saveChanges(form.getFieldsValue(), filesToSave);
    } else await saveChanges(fieldData, filesToSave);
  };

  const handleFieldUpdate = async (fieldName: string, customValue?: any) => {
    try {
      // Obtener el valor del campo
      const fieldValue = customValue !== undefined
        ? customValue
        : form.getFieldValue(fieldName);

      if (!fieldValue) return;

      await form.validateFields([fieldName]);

      const fieldData = { [fieldName]: fieldValue };

      const isComplete = isFormComplete();

      if (isComplete) {
        await saveResponse(form.getFieldsValue(), true);
      } else await saveResponse(fieldData, false);

    } catch (error) {
      console.log(`❌ Error de validación en campo: ${fieldName}`, error);
    }
  };

  const deleteFile = (file: File) => {
    _deleteFile({ fileKey: file.key, taskResponseId: ownership.key });
    saveChanges(form.getFieldsValue(), null, TaskState.Incomplete);
  };

  const isDisabledItem = () => {
    return disabledForm || fetching || loadingDeleteFile ||
      (!!ownership._id && ownership.state === TaskState.Incomplete && !isEditVerify && isValidForm(ownership, ownership.files));
  }

  const isValidForm = (values: any, files: any) => {
    return (values.address || ownership.address) &&
      (values.dateBirth || ownership.dateBirth) &&
      (values.email || ownership.email) &&
      (values.phone || ownership.phone) &&
      (values.ssn || ownership.ssn) &&
      (files?.length > 0 || ownership?.files?.length > 0) &&
      !!(values.ownership || ownership.ownership);
  }

  const addCheckIsDataSuccess = (label: any) => {
    return (
      <div>
        {!isTextDataError && ownership.state === TaskState.RequestError &&
          <img width={12} src={checkGreenSvg} alt="check" />
        }{' '}
        <span>{label}</span>
      </div>
    );
  }

  const issue = taskComments?.find(item => item.taskResponse._id === ownership.taskResponse._id && ownership.state === TaskState.RequestError);
  let textDataError = issue?.metadata?.dataOrDocumentSelected.includes('Text/data');

  //Tener la respuesta de los comments primero, para inicializar el formulario con la logica inicial de los errores
  if (!taskComments)
    return null;

  return (
    <Form
      form={form}
      name={`edit-${ownership?._id || Math.random()}`}
      autoComplete="off"
      className="form-edit"
      requiredMark="optional"
      colon={false}
    >

      <Form.Item
        label={addCheckIsDataSuccess("SSN" + (form.getFieldValue("ssn") ? "" : "*"))}
        name="ssn"
        className="ssn"
        validateTrigger="onBlur"
        rules={[
          { required: true, message: 'Field is missing' },
          { min: 11, message: 'Field is invalid' }
        ]}
      >
        <InputBorderNone
          placeholder="239-23-2323"
          height={20}
          style={{ marginLeft: !isTextDataError && ownership.state === TaskState.RequestError ? 15 : 0 }}
          className="bold-lith"
          disabled={isDisabledItem()}
          onChange={formatterSsn}
          onBlur={() => handleFieldUpdate("ssn")}
        />
      </Form.Item>

      <Form.Item
        label={addCheckIsDataSuccess("Date of birth" + (form.getFieldValue("dateBirth") ? "" : "*"))}
        name="dateBirth"
        className="datebirth"
        validateTrigger="onBlur"
        rules={[
          { required: true, message: 'Field is missing' },
          { validator: validatorDateError }
        ]}
      >
        <DateInput
          placeholder="12/31/1997"
          format={'MM/dd/yyyy'}
          style={{
            marginLeft: !isTextDataError && ownership.state === TaskState.RequestError ? 15 : 0,
            outline: 'none'
          }}
          className="bold-lith input-transparent"
          disabled={isDisabledItem()}
          onBlur={() => handleFieldUpdate("dateBirth")}
        />
      </Form.Item>
      <Form.Item
        label={addCheckIsDataSuccess("Phone number" + (form.getFieldValue("phone") ? "" : "*"))}
        name="phone"
        className="phone"
        validateTrigger="onBlur"
        rules={[
          { required: true, message: 'Field is missing' },
          { min: 12, message: 'Field is missing' }
        ]}
      >
        <InputBorderNone
          placeholder="203-423-3534"
          height={20}
          style={{ marginLeft: !isTextDataError && ownership.state === TaskState.RequestError ? 15 : 0 }}
          className="bold-lith"
          disabled={isDisabledItem()}
          onChange={formatterPhone}
          onBlur={() => handleFieldUpdate("phone")}
        />
      </Form.Item>

      <Form.Item
        label={addCheckIsDataSuccess("Email address" + (form.getFieldValue("email") ? "" : "*"))}
        name="email"
        className="email"
        rules={[{ required: true, type: 'email', message: 'Invalid email address' }]}
      >
        <InputBorderNone
          type="email"
          placeholder="lend@constlending.com"
          height={20}
          style={{ marginLeft: !isTextDataError && ownership.state === TaskState.RequestError ? 15 : 0 }}
          disabled={isDisabledItem()}
          color={'bold-lith'}
          onBlur={() => handleFieldUpdate("email")}
        />
      </Form.Item>

      <Form.Item
        shouldUpdate
        label={addCheckIsDataSuccess("Home address" + (form.getFieldValue("address") ? "" : "*"))}
        name="address"
        className="address"
        validateDebounce={500}
        rules={[
          { required: true, message: 'Field is missing' },
          { min: 3, message: 'Field is missing' },
          { max: 100, message: 'Field is missing' }
        ]}
      >
        <InputAutocompleteAddress
          disabled={isDisabledItem()}
          style={{
            marginLeft: !isTextDataError && ownership.state === TaskState.RequestError ? 15 : 0
          }}
          initialValue={issue && textDataError ? '' : ownership.address}
          onSelect={async ({ formatted_address }: any) => {
            form.setFieldValue('address', formatted_address);
            await handleFieldUpdate("address", formatted_address);
          }}
        />
      </Form.Item>

      <span className="textedit textedit-ab">
        {saveEdit === 'loading' && (
          <>
            <img src={iconSpinnerPurple} alt="upload" className="upload" width={10} />
            <span style={{ color: '#A278B3' }}>SAVING</span>
          </>
        )}
        {saveEdit === 'saved' && (
          <>
            <img src={fileCheckSvg} alt="upload" className="upload" width={12} />
            <span style={{ color: '#6B956E' }}>EDITS SAVED</span>
          </>
        )}
      </span>

      <div className="identification">
        <label>{`Driver’s License`}</label>
        {ownership.files?.filter(file => file.state !== 'request_error').map((file: File, index: number) => (
          <InputUploadedFile
            key={index}
            file={file}
            isActionSeeFile
            deleteFile={
              !fetching &&
              (ownership.state === TaskState.Incomplete || ownership.state === TaskState.Draft ? deleteFile : undefined)
            }
            loadingDeleteFile={loadingDeleteFile || fetching}
            state={
              ownership.state === TaskState.Incomplete ? 'DRAFT' :
                ownership.state === TaskState.Draft ? ownership.state : (file.state as any)
            }
          />
        ))}
        {(
          !ownership.files ||
          ownership.files.length === 0 ||
          ownership.files[ownership.files.length - 1].state === 'request_error'
        ) && (
            <UploadDocs
              numberOfFiles={1}
              listFileUpload={listFileUpload}
              setListFileUpload={setListFileUpload}
              disabled={isDisabledItem()}
            />
          )}
      </div>
      {!isEditVerify && ownership._id && isValidForm(ownership, ownership.files) && ownership.state === TaskState.Incomplete &&
        <div className='submit verify-submits'>
          <Button
            className="button button-submit button-yellow"
            onClick={verifyProfile}
          >
            Confirm profile
          </Button>
          <Button
            className="button button-submit button-edit"
            onClick={() => setEditVerify(true)}
          >
            Edit profile
          </Button>
        </div>
      }

      {
        //Condición negativa de los botones Verify profile y Edit profile
        !(!isEditVerify && ownership._id && isValidForm(ownership, ownership.files) && ownership.state === TaskState.Incomplete) &&
        ownership.state !== TaskState.Approved && ownership.state !== TaskState.Review &&
        (
          <Form.Item className="submit" shouldUpdate>
            {() =>
              <ModalSubmitProfile
                ownerships={ownerships}
                ownerState={ownership.state as TaskState}
                fetching={fetching}
                disabled={isFormDisabled()}
                onClick={() => saveResponse(form.getFieldsValue(), true)}
                messageErrorDisabled='Please complete all fields and documents to submit for review'
              />
            }
          </Form.Item>
        )}
    </Form>
  );
};
export default FormEdit;
